import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  IconButton,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  Divider,
  Button,
  Chip,
} from '@mui/material';
import {
  ArrowBack,
  ExpandMore,
  Search,
  RocketLaunch,
  AccountTree,
  Architecture,
  SmartToy,
  Security,
  Email,
  HelpOutline,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const faqSections = [
  {
    title: 'Getting Started',
    icon: <RocketLaunch />,
    color: '#667eea',
    faqs: [
      {
        question: 'How do I log in to the EIS Platform?',
        answer:
          'You can log in using your email and password on the login page. If your organization uses SSO, click "Sign in with Google" or "Sign in with GitHub". For first-time users, you will need to verify your email address before accessing the platform.',
      },
      {
        question: 'How do I create a new project?',
        answer:
          'Navigate to the Projects section from the sidebar and click the "New Project" button. Fill in the project name, description, and select the group it belongs to. The project will be created with a default "main" branch ready for use.',
      },
      {
        question: 'How do I invite team members?',
        answer:
          'Go to Settings > Administration (admin users only) to manage roles. Team members can be invited by an admin through the Role Management panel. They will receive an email invitation to join the platform and will be assigned to the appropriate group.',
      },
      {
        question: 'How do I navigate the platform?',
        answer:
          'The left sidebar provides access to all major sections: Projects, Design / CAD, Requirements, Documents, Orchestrator, and Settings. Use the project switcher in the header to change projects and branches.',
      },
      {
        question: 'Where can I get additional help?',
        answer:
          'You can reach our support team at support@evolunic.com. The AI Assistant (e-pilot) can also answer questions about platform features and engineering workflows. Check the Changelog page for the latest feature updates.',
      },
    ],
  },
  {
    title: 'Projects & Version Control',
    icon: <AccountTree />,
    color: '#764ba2',
    faqs: [
      {
        question: 'What is version control and why should I care?',
        answer:
          'Version control automatically tracks every change to every file in your project. Instead of saving files as "Drawing_v2_FINAL_John.dwg", the platform keeps a clean history of all revisions with timestamps and author names. Think of it like the revision history in SolidWorks PDM or Windchill \u2014 but built into the platform. You can always go back to any previous version, see who changed what, and multiple engineers can work on the same project without overwriting each other.',
      },
      {
        question: 'How do branches work?',
        answer:
          'Each project has a "main" branch, which is the official released baseline \u2014 like the "Released" state in a PDM vault. To make changes, you create a "feature branch" (e.g., "motor-redesign"). Your branch starts as a copy of main, so you have all existing files. You upload and modify files on your branch without affecting main. When your work is ready, you merge it back to main. Think of it like checking out files to a personal workspace, working on them, and then checking them back in.',
      },
      {
        question: 'Why can\'t I upload files directly to the main branch?',
        answer:
          'Main is a protected branch \u2014 it represents the approved, released state of your project. This prevents accidental overwrites and ensures all changes go through a proper review process. To make changes: (1) Create a feature branch, (2) Upload files to that branch, (3) Commit your changes, (4) Merge back to main. This is the same principle as "released vs. working" states in PDM systems like Teamcenter or Windchill.',
      },
      {
        question: 'What is the typical workflow for making changes?',
        answer:
          'The standard workflow has four steps: (1) Create a branch \u2014 go to Branch Management and click "New Branch", give it a descriptive name like "pcb-layout-update". (2) Upload files \u2014 switch to your new branch using the branch selector in the header, then upload or import your files. They appear as "Pending Changes". (3) Commit \u2014 click "Commit" in the Pending Changes panel, write a message describing what you changed and why (e.g., "Updated PCB layout per EMC review feedback"). (4) Merge \u2014 go to Branch Management, click "Merge Branches", select your branch as source and main as target, preview the changes, and merge.',
      },
      {
        question: 'How do I stage files for commit?',
        answer:
          'When you upload a file to a feature branch, it is automatically "staged" \u2014 meaning it appears in the Pending Changes panel at the top of the file list. Staged files are like files in an outbox waiting to be submitted. You can: review which files are staged and what action each one represents (Added, Modified, or Deleted), unstage a file if you change your mind (click the undo icon), or commit all staged files at once with a description. This is similar to the check-in queue in SolidWorks PDM.',
      },
      {
        question: 'How do I commit changes?',
        answer:
          'Click the "Commit" button in the Pending Changes panel. Write a clear, descriptive commit message \u2014 for example: "Increased wall thickness on housing per stress analysis" or "Added thermal pad footprint to PCB layout". A commit captures a snapshot of all your staged files at that moment, records who made the changes and when, and creates a permanent entry in the branch history. Think of it like creating a new revision (Rev A \u2192 Rev B) in a PDM system. After committing, the Pending Changes panel shows "Ready to merge".',
      },
      {
        question: 'How do I merge branches?',
        answer:
          'Go to Branch Management and click "Merge Branches". Select your feature branch as the source and main (or another branch) as the target. Click "Preview Merge" \u2014 the system compares both branches and shows you exactly what will change: files added, modified, or removed. If there are no conflicts, click "Merge" to complete. The merge creates a permanent record linking the source and target branches. After merging, your changes are part of main (the released baseline). This is similar to promoting files from a working area to the released vault in a PLM system.',
      },
      {
        question: 'What happens when there are merge conflicts?',
        answer:
          'A conflict occurs when the same file was modified on both branches. For example: you updated "Motor_Assembly.step" on your branch, but a colleague also updated it on main. The platform detects this during merge preview and flags it. You have three options: keep your version (source), keep the other version (target), or upload a manually resolved file. All conflict resolutions are tracked with the resolver name and timestamp. The merge cannot proceed until all conflicts are resolved.',
      },
      {
        question: 'Can multiple people work on the same project at the same time?',
        answer:
          'Yes. Each engineer creates their own feature branch and works independently. For example, one engineer works on "motor-redesign" while another works on "enclosure-update". Since branches are isolated, changes do not interfere with each other. When both are ready, they merge their branches to main one at a time. If both modified the same file, the system detects the conflict during the second merge and asks for resolution.',
      },
      {
        question: 'How do I see who changed what?',
        answer:
          'Go to the Project Activity tab in your project. It shows a timeline of every action: file uploads, commits, branch creation, merges, and deletions. Each entry shows who performed the action, when, and what was affected. You can also view commit history in the Branch Management tab to see the full revision history of a branch. This audit trail is essential for design reviews, quality management (ISO 9001, AS9100), and regulatory compliance.',
      },
      {
        question: 'What happens to my files when I delete a branch?',
        answer:
          'When you delete a feature branch, only the files and changes that were exclusive to that branch are removed. If you already merged the branch to main, those files are safely part of main and are not affected. If the branch was never merged, its changes are lost \u2014 similar to discarding a working copy in a PDM system. The system warns you before deleting and will not allow you to delete the main branch.',
      },
    ],
  },
  {
    title: 'CAD & Design',
    icon: <Architecture />,
    color: '#e91e63',
    faqs: [
      {
        question: 'What CAD file formats are supported?',
        answer:
          'The platform supports a wide range of CAD formats including DWG, DXF, STEP, IGES, STL, OBJ, Parasolid, SolidWorks (.sldprt, .sldasm), CATIA, Creo/Pro-E, NX, Fusion 360, Inventor, and more. Files are processed through Autodesk Platform Services (APS) for viewing and analysis.',
      },
      {
        question: 'How does the CAD viewer work?',
        answer:
          'The CAD Viewer uses Autodesk Forge Viewer to render 2D and 3D models directly in your browser. Uploaded CAD files are translated through APS Model Derivative service. You can rotate, zoom, measure, and inspect models without installing any desktop software.',
      },
      {
        question: 'How does AI-powered CAD drawing generation work?',
        answer:
          'The Drawing Agent uses AI to generate CAD drawings from natural language descriptions. Describe what you need (e.g., "Draw a UIC60 rail cross-section profile") and the agent will create a dimensionally accurate drawing using an engineering knowledge base. You can iteratively refine the drawing with follow-up modification instructions.',
      },
      {
        question: 'How do I export CAD files?',
        answer:
          'From the CAD Viewer, use the export options to download files in various formats. Generated drawings can be downloaded as DXF files. For Forge Viewer models, you can access the original source file or request translation to additional output formats through the Model Derivative API.',
      },
    ],
  },
  {
    title: 'AI Assistant',
    icon: <SmartToy />,
    color: '#00bcd4',
    faqs: [
      {
        question: 'What are the e-pilot modes?',
        answer:
          'The e-pilot AI Assistant operates in two modes: General mode for platform questions, file analysis, and engineering knowledge; and CAD mode for drawing generation and modification. Toggle CAD mode in the chat header, or it auto-enables when viewing Design / CAD.',
      },
      {
        question: 'How does AI document generation work?',
        answer:
          'Ask e-pilot to generate documents from your project data (e.g., "Generate a design review report"). The AI pulls data from your requirements and CAD models to produce structured documents. Supported outputs include technical reports, requirement summaries, and BOM exports.',
      },
      {
        question: 'Does the AI use a knowledge base?',
        answer:
          'Yes. The Drawing Agent uses a built-in engineering knowledge base containing railway component geometry (25+ rail profiles, 16 fastening systems, 15+ turnouts), design rules for speed/load conditions, and pre-built assembly templates. This ensures dimensionally accurate, standards-compliant drawings.',
      },
    ],
  },
  {
    title: 'Account & Security',
    icon: <Security />,
    color: '#ff9800',
    faqs: [
      {
        question: 'How do I change my password?',
        answer:
          'Go to Settings and scroll to the "Change Password" section. Enter your current password and your new password (minimum 8 characters). Click "Update Password" to save. If you forgot your password, use the "Forgot Password" link on the login page to receive a reset email.',
      },
      {
        question: 'How do I enable Multi-Factor Authentication (MFA)?',
        answer:
          'Navigate to Settings > Security. Click "Enable 2FA" to set up Time-Based One-Time Password (TOTP) authentication. Scan the QR code with an authenticator app (Google Authenticator, Authy, etc.) and enter the verification code. Save your recovery codes in a secure location.',
      },
      {
        question: 'What are the different user roles?',
        answer:
          'The platform has three roles: Engineer (standard access to projects and tools), Manager (can manage team members and approve changes), and Admin (full platform administration including role management, group configuration, and system settings). Roles are assigned by admins in the Administration panel.',
      },
      {
        question: 'Can I export my data?',
        answer:
          'Yes. You can download individual files from any project branch. For bulk export, admins can use the API to retrieve project data, file manifests, and metadata. CAD files, documents, and generated reports can all be downloaded from their respective views.',
      },
    ],
  },
];

const HelpCenterPage = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [expandedPanel, setExpandedPanel] = useState(false);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : false);
  };

  // Filter FAQs based on search text
  const filteredSections = faqSections
    .map((section) => ({
      ...section,
      faqs: section.faqs.filter(
        (faq) =>
          faq.question.toLowerCase().includes(searchText.toLowerCase()) ||
          faq.answer.toLowerCase().includes(searchText.toLowerCase())
      ),
    }))
    .filter((section) => section.faqs.length > 0);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{ mr: 2 }}
          aria-label="Go back"
        >
          <ArrowBack />
        </IconButton>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Help Center
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Find answers to common questions about the EIS Platform
          </Typography>
        </Box>
      </Box>

      {/* Search Bar */}
      <Card
        sx={{
          mb: 4,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#fff',
        }}
      >
        <CardContent sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            How can we help you?
          </Typography>
          <TextField
            fullWidth
            placeholder="Search FAQs..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            variant="outlined"
            sx={{
              maxWidth: 500,
              mx: 'auto',
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(255,255,255,0.95)',
                borderRadius: 2,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {/* FAQ Sections */}
      {filteredSections.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <HelpOutline sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No results found for "{searchText}"
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try different keywords or contact support below.
          </Typography>
        </Box>
      )}

      {filteredSections.map((section) => (
        <Box key={section.title} sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: section.color,
                color: '#fff',
                mr: 1.5,
              }}
            >
              {section.icon}
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {section.title}
            </Typography>
            <Chip
              label={`${section.faqs.length}`}
              size="small"
              sx={{ ml: 1, bgcolor: 'action.hover' }}
            />
          </Box>

          {section.faqs.map((faq, faqIndex) => {
            const panelId = `${section.title}-${faqIndex}`;
            return (
              <Accordion
                key={panelId}
                expanded={expandedPanel === panelId}
                onChange={handleAccordionChange(panelId)}
                sx={{
                  mb: 1,
                  '&:before': { display: 'none' },
                  boxShadow: 1,
                  borderRadius: '8px !important',
                  overflow: 'hidden',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    '& .MuiAccordionSummary-content': { my: 1.5 },
                  }}
                >
                  <Typography sx={{ fontWeight: 500 }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.7 }}
                  >
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      ))}

      {/* Contact Support */}
      <Divider sx={{ my: 4 }} />
      <Card sx={{ textAlign: 'center' }}>
        <CardContent sx={{ py: 4 }}>
          <Email
            sx={{ fontSize: 40, color: '#667eea', mb: 1 }}
          />
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Still need help?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Our support team is ready to assist you with any questions.
          </Typography>
          <Button
            variant="contained"
            href="mailto:support@evolunic.com"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              textTransform: 'none',
              px: 4,
            }}
          >
            Contact support@evolunic.com
          </Button>
        </CardContent>
      </Card>
    </Container>
  );
};

export default HelpCenterPage;
